import Seo from "@/components/Seo";
import { PageHero, Section, Btn, SectionHead, Stat } from "@/components/kit";
import { Reveal, Eyebrow } from "@/lib/anim";
import { STATS_HOME, BRANDS, BRAND_DISCLAIMER, SITE } from "@/lib/content";
import { IMG } from "@/lib/images";

const TIMELINE = [
  ["Department store buying", "India, 78-store network", "Womenswear categories across a national store network worth roughly $35M, with eight seasonal drops a year and a seven-stage sampling process."],
  ["E-commerce private label", "ASOS-backed platform", "The largest revenue category on the platform, buying 150 or more new styles a week and building a direct factory base across four countries."],
  ["Omnichannel category P&L", "GCC retail group", "Full profit and loss across womenswear, footwear, jewellery and accessories, the largest category managed at around $60M, onboarding brands and developing licensed ranges."],
  ["Founder, consumer brand", "Dubai eveningwear", "A brand built from nothing: the costing and sourcing architecture, a 40-plus vendor base, and 300-plus products on a test-and-scale model."],
  ["Prox", "South Melbourne, today", "A buyer-side advisory for founders and growing consumer brands in Australia and New Zealand."],
];

export default function About() {
  return (
    <>
      <Seo title="About — Sourcing & Fashion Buying Consultant, Melbourne" canonical="/about" description="Prox is a buyer-side product sourcing, costing and fashion buying advisory in Melbourne for founders and growing consumer brands, built on thirteen years of global buying and sourcing." keywords="sourcing consultant Melbourne, fashion buying consultant Australia, fashion consultant Melbourne, procurement consultant Australia, fashion business consultant Australia, sourcing consultant Australia" />
      <PageHero
        eyebrow="About Prox"
        crumbs={[{ label: "Home", to: "/" }, { label: "About" }]}
        title="A buyer-side advisory for founders and growing consumer brands."
        lead="Prox helps businesses source the right products, price them properly and launch with confidence. The advice is practical because it comes from years spent on the buying side, not the sidelines."
      />

      <Section bg="bone">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 prose max-w-none">
            <p>Prox exists because most consumer brands do not need another design opinion or another marketing agency. They need someone who has sat on the buying side, who can read a costing sheet and a sell-through report, and who is paid only by the client.</p>
            <p>The practice was founded by Rasika Khode after thirteen years buying and building product for retailers and e-commerce platforms, from the factory floor to a full category profit and loss, across six sourcing countries. That work included costing ranges, negotiating with factories, planning buys, running sampling and quality, clearing aging stock, and launching categories from nothing, as well as building and running a consumer brand from scratch.</p>
            <p>The result is a single, senior point of contact who understands the founder's version of every problem: the first order that is too big, the quote that cannot be benchmarked, the bestseller that runs out while the rest of the range sits.</p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden" style={{ borderRadius: 2 }}>
              <img src={IMG.studio} alt="A product studio work table" className="img-cover duo" loading="lazy" />
            </div>
          </div>
        </div>
      </Section>

      <Section bg="charcoal" dark pad={false} className="py-14 md:py-16">
        <p className="eyebrow mb-8">The numbers behind the experience</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
          {STATS_HOME.map((s, i) => <Reveal i={i} key={s.c}><Stat n={s.n} c={s.c} dark /></Reveal>)}
        </div>
      </Section>

      <Section bg="sand">
        <SectionHead eyebrow="Track record" title="Where the experience comes from." />
        <div className="mt-12 border-t border-[var(--stone)]">
          {TIMELINE.map((t, i) => (
            <Reveal i={i % 2} key={t[0]}>
              <div className="grid md:grid-cols-12 gap-4 py-8 border-b border-[var(--stone)]">
                <div className="md:col-span-3">
                  <h3 className="t-h4">{t[0]}</h3>
                  <p className="font-mono text-[12px] uppercase tracking-wide text-[var(--muted)] mt-1">{t[1]}</p>
                </div>
                <p className="md:col-span-9 t-body text-[var(--muted)]">{t[2]}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="t-sm text-[var(--muted)] mt-8 measure">These engagements were carried out as an employee of the businesses described, not as Prox client work. Prox is a new practice, and client case studies will be added as engagements complete.</p>
      </Section>

      <Section bg="bone">
        <Eyebrow>Product bought and developed</Eyebrow>
        <div className="flex flex-wrap gap-x-8 gap-y-3 mt-2">
          {BRANDS.map((b) => <span key={b} className="font-serif text-2xl md:text-3xl">{b}</span>)}
        </div>
        <p className="t-sm text-[var(--muted)] mt-8 measure">{BRAND_DISCLAIMER}</p>
      </Section>

      <Section bg="clay" dark className="py-20 md:py-24">
        <div className="max-w-3xl">
          <h2 className="t-display2">Work with a senior specialist.</h2>
          <p className="t-lead mt-6" style={{ color: "rgba(255,255,255,0.85)" }}>Prox caps the number of active clients so the work stays hands-on. Start with a free call.</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Btn to="/book" variant="bone">Book a call</Btn>
            <Btn href={SITE.linkedin} variant="outline-bone">Connect on LinkedIn</Btn>
          </div>
        </div>
      </Section>
    </>
  );
}
